import { Link } from "@tanstack/react-router";
import { useCart } from "../context/CartContext";
import { useState, useEffect, useRef } from "react";

const LOGO_URL = "https://res.cloudinary.com/dnggmrgmu/image/upload/w_200,f_auto,q_auto/v1782224263/logo-transparent-DpiMWMB1_nkc6ji.png";

const MARQUEE_TEXT = [
  "new drop just dropped",
  "★",
  "fresh off the rack",
  "★",
  "zamalek's worst kept secret",
  "★",
  "no returns but it's worth it trust",
  "★",
  "buy it before ur friend does",
  "★",
];

export function Header() {
  const { count } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bump, setBump] = useState(false);
  const prevCount = useRef(count);

  useEffect(() => {
    if (count > prevCount.current) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 380);
      prevCount.current = count;
      return () => clearTimeout(t);
    }
    prevCount.current = count;
  }, [count]);

  return (
    <>
      {/* Announcement marquee */}
      <div className="overflow-hidden border-b border-border bg-background py-2">
        <div
          className="marquee-track flex w-max items-center gap-6 text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
          style={{ animation: "marquee 30s linear infinite" }}
        >
          {[...MARQUEE_TEXT, ...MARQUEE_TEXT].map((t, i) =>
            t === "★" ? (
              <span key={i} className="h-1.5 w-1.5 rounded-full bg-foreground animate-pulse-dot" />
            ) : (
              <span key={i} className="whitespace-nowrap">{t}</span>
            )
          )}
        </div>
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          {/* Hamburger (mobile) */}
          <button
            className="flex h-8 w-8 flex-col items-start justify-center gap-1.5 sm:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span className={`block h-px w-6 bg-foreground transition-all duration-200 ${menuOpen ? "translate-y-2 rotate-45" : ""}`} />
            <span className={`block h-px w-4 bg-foreground transition-all duration-200 ${menuOpen ? "opacity-0" : ""}`} />
            <span className={`block h-px w-6 bg-foreground transition-all duration-200 ${menuOpen ? "-translate-y-2 -rotate-45" : ""}`} />
          </button>

          {/* Desktop nav — left */}
          <nav className="hidden items-center gap-8 text-[11px] uppercase tracking-[0.2em] sm:flex">
            {[
              { to: "/", label: "Home" },
              { to: "/shop", label: "Shop" },
              { to: "/about", label: "About" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                activeOptions={{ exact: true }}
                activeProps={{ className: "relative text-foreground after:absolute after:-bottom-1 after:left-0 after:h-px after:w-full after:bg-foreground" }}
                inactiveProps={{ className: "relative text-muted-foreground transition-colors hover:text-foreground after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-foreground after:transition-all after:duration-300 hover:after:w-full" }}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Logo — center */}
          <Link to="/" className="absolute left-1/2 -translate-x-1/2">
            <img src={LOGO_URL} alt="Mr. Pizza Steve" className="h-12 w-auto sm:h-14" />
          </Link>

          {/* Cart — right */}
          <Link
            to="/cart"
            className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className={`inline-block ${bump ? "animate-cart-bump" : ""}`}>
              {count > 0 ? `Cart (${count})` : "Cart"}
            </span>
          </Link>
        </div>

        {/* Mobile menu */}
        <nav
          className="overflow-hidden border-t border-border bg-background sm:hidden transition-[grid-template-rows] duration-300 ease-out grid"
          style={{ gridTemplateRows: menuOpen ? "1fr" : "0fr", borderTopWidth: menuOpen ? 1 : 0 }}
        >
          <div className="min-h-0 overflow-hidden px-4">
            {[
              { to: "/", label: "Home" },
              { to: "/shop", label: "Shop" },
              { to: "/about", label: "About" },
              { to: "/cart", label: count > 0 ? `Cart (${count})` : "Cart" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="block py-3 text-[13px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground border-b border-border last:border-0 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>
    </>
  );
}

export function Footer() {
  return (
    <footer className="mt-32 border-t border-border">
      <div className="mx-auto grid max-w-6xl gap-12 px-4 py-16 sm:grid-cols-3 sm:px-6">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-foreground">
            Mr. Pizza Steve Finds
          </p>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            Curated vintage & thrift by Steve dos Santos.<br />Zamalek, Cairo.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">Shipping via Turbo · 3–5 working days</p>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">Visit</p>
          <p className="text-xs leading-relaxed text-foreground">
            30 Hassan Assem St<br />Zamalek, Cairo
          </p>
          <p className="mt-2 text-xs text-muted-foreground">Daily · 3pm – 11pm</p>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">Follow</p>
          <a
            href="https://instagram.com/mr.pizzastevefinds"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-foreground hover:text-muted-foreground transition-colors"
          >
            @mr.pizzastevefinds
          </a>
          <p className="mt-2 text-xs text-muted-foreground">Drops post here first</p>
          <div className="mt-6 flex gap-4">
            <Link to="/shop" className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors">All Products</Link>
            <Link to="/about" className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors">About</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-border py-5 text-center text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        © {new Date().getFullYear()} Pizza Steve Energy
      </div>
    </footer>
  );
}

/** @deprecated use Header's built-in marquee */
export function Marquee({ text }: { text: string }) {
  const items = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className="overflow-hidden border-y border-border">
      <div
        className="flex w-max gap-8 py-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
        style={{ animation: "marquee 22s linear infinite" }}
      >
        {items.concat(items).map((_, i) => (
          <span key={i} className="flex items-center gap-8 whitespace-nowrap">
            {text} <span>✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** @deprecated */
export function RotatingBanner() { return null; }
